import React, { useMemo, useState } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { Text } from 'react-native-paper';

import { Palette, radius, spacing, useThemePalette } from '../../theme';
import DatePicker from '../DatePicker';

interface DateFieldProps {
  label: string;
  value: string;
  onChange: (iso: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

const parseDate = (iso: string): Date | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
};

const DateField: React.FC<DateFieldProps> = ({
  label,
  value,
  onChange,
  disabled,
  placeholder,
}) => {
  const palette = useThemePalette();
  const styles = useMemo(() => makeStyles(palette), [palette]);
  const [open, setOpen] = useState(false);

  const parsed = parseDate(value);
  const display = parsed
    ? `${String(parsed.getMonth() + 1).padStart(2, '0')}/${parsed.getFullYear()}`
    : placeholder ?? '—';

  return (
    <View>
      <Text style={styles.label}>{label}</Text>
      <TouchableOpacity
        style={[styles.chip, disabled && styles.chipDisabled]}
        onPress={() => !disabled && setOpen(true)}
        activeOpacity={0.85}
        disabled={disabled}
      >
        <Text style={[styles.chipText, !parsed && styles.placeholder]}>{display}</Text>
      </TouchableOpacity>
      <DatePicker
        isVisible={open}
        onCancel={() => setOpen(false)}
        onConfirm={(d) => {
          onChange(d.toISOString());
          setOpen(false);
        }}
        initialDate={parsed ?? new Date()}
        minimumDate={new Date(1960, 0, 1)}
        maximumDate={new Date(new Date().getFullYear() + 10, 11, 31)}
      />
    </View>
  );
};

const makeStyles = (p: Palette) =>
  StyleSheet.create({
    label: {
      fontSize: 12,
      fontWeight: '600',
      color: p.textMuted,
      marginBottom: spacing.xs,
    },
    chip: {
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.sm + 2,
      borderRadius: radius.md,
      backgroundColor: p.surface,
      borderWidth: 1,
      borderColor: p.border,
    },
    chipDisabled: { opacity: 0.45 },
    chipText: { color: p.textPrimary, fontSize: 14 },
    placeholder: { color: p.textMuted },
  });

export default DateField;
